
import { Component, ElementRef, Input, OnInit, ViewChild, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { InventoryItemDetail, InventoryItem, SellerOfferAction, SellerOfferDto, LaunchDarklyService, multiLanguageAlphaNumericRegexHelper } from 'remarketing-angular-library';
import { takeUntil } from 'rxjs/operators';
import { get } from 'lodash';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { OfferActionInterface } from '../../interfaces/offer-action.interface';
import { InventoryItemDataService } from './../../../sell-shared/services/inventory-item-data.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SaComponent, AnalyticsService, CurrencyFormatService } from 'smartauction-frontend-micro-library';
import {
  CharacterCountComponent, CharacterCountDirective, CurrencyInputComponent,
  DatalistItemComponent,
  InputConfig
} from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'lib-counter',
    templateUrl: './counter.component.html',
    styleUrls: ['./counter.component.scss'],
    imports: [
        TranslocoDirective,
        DatalistItemComponent,
        ReactiveFormsModule,
        CharacterCountComponent,
        CurrencyInputComponent,
        CommonModule,
        CharacterCountDirective
    ]
})
export class CounterComponent extends SaComponent implements OnInit {
  private _isVisible = false;
  characterCountData: any;
  @Input() set isVisible(isVisible: boolean) {
    this._isVisible = isVisible;
    if (this._isVisible) {
      setTimeout(() => this.heading.nativeElement.focus());
    }
  }
  get isVisible() {
    return this._isVisible;
  }
  @ViewChild('heading') heading: ElementRef<HTMLHeadingElement>;
  @Input() inventoryItem: InventoryItem;
  @Input() offer: Partial<SellerOfferDto>;
  @Input() tenantName: string;
  @Output() cancel = new EventEmitter();
  @Output() action = new EventEmitter<OfferActionInterface>();

  counterForm: UntypedFormGroup;
  incorrectAmount = false;
  isAmountEmpty = false;
  localErrorMessage: string;
  localErrorState$: Observable<{ message: string; offerUuid: string }> = this.inventoryItemDataService.localErrorState;
  counterOfferAmountError: string;
  emptyOfferAmountError = '';

  inputConfig: InputConfig = {
    inputClass: 'form-control',
    inputName: 'counterOfferAmount',
    inputFormControlName: 'counterOfferAmount',
    inputAttrAriaLabelledBy: 'counter-offer-amount-label counter-offer-amount-error',
    thousandSeparator: '',
    isFrenchCanadian: this.isFrenchCanadian,
    inputInvalidClass: this.invalidClass,
    localErrorMessage: ''
  };

  get invalidClass() {
    return this.incorrectAmount || this.isAmountEmpty;
  }

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly analyticsService: AnalyticsService,
    private readonly inventoryItemDataService: InventoryItemDataService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly translocoService: TranslocoService
  ) {
    super();
  }

  ngOnInit() {
    this.getTranslations();
    this.counterForm = this.formBuilder.group({
      offerComment: [''],
      counterOfferAmount: ['', Validators.required]
    });

    this.localErrorState$.pipe(takeUntil(this.destroyed$)).subscribe(localErrorState => {
      if (localErrorState.offerUuid) {
        localErrorState.offerUuid === this.offer.offerUuid
          ? (this.localErrorMessage = localErrorState.message)
          : (this.localErrorMessage = null);
        this.inputConfig.localErrorMessage = this.localErrorMessage;
      } else {
        this.localErrorMessage = null;
      }
    });
  }

  getTranslations() {
    const keyParams = {
      error: {},
      UIComponents: {},
      'common.currency': {}
    };
    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translatedObj => {
        this.emptyOfferAmountError = translatedObj[0].enterValidAmount;
        this.counterOfferAmountError = translatedObj[0].counterOfferAmountError;
        this.characterCountData = translatedObj[1].characterCount;
        this.inputConfig.thousandSeparator = translatedObj[2].thousandSeparator;
        this.inputConfig.isFrenchCanadian = this.isFrenchCanadian;
      });
  }

  get title() {
    return `${this.detail('modelYear')} ${this.detail('make')} ${this.detail('model')} ${this.detail(
      'trim'
    )} VIN ${this.detail('vin')}`;
  }

  detail(key: keyof InventoryItemDetail): string {
    return get(this.itemDetails, key, '');
  }

  get itemDetails(): any | {} {
    return get(this.inventoryItem, 'itemDetail', {});
  }

  get(path: string, otherwise: any = 'N/A'): string {
    return get(this.offer, path, otherwise);
  }

  get isCanadianTenantEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get isFrenchCanadian(): boolean {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return frenchLanguage && this.isCanadianTenantEnabled;
  }

  replaceInvalidCharacters(e) {
    e.target.value = multiLanguageAlphaNumericRegexHelper(e.target.value);
  }

  counter() {
    this.validateAmount();
    if (this.counterForm.valid && !this.incorrectAmount && !this.isAmountEmpty) {
      const payload = {
        offerUuid: this.offer.offerUuid,
        requestedAction: SellerOfferAction.COUNTER,
        comment: this.counterForm.get('offerComment').value,
        amount: parseInt(this.counterForm.get('counterOfferAmount').value)
      };
      const analyticsPayload = {
        formValues: {
          name: 'Counter Offer',
          status: null,
          errors: null,
          type: '',
          step: '1',
          stepname: '',
          items: [
            {
              allow: 'yes',
              error: null,
              name: 'Vehicle UUID',
              track: 'yes',
              value: this.inventoryItem ? this.inventoryItem.inventoryItemUuid : null
            },
            {
              allow: 'yes',
              error: null,
              name: 'Buyer Offer',
              track: 'yes',
              value: parseInt(this.counterForm.get('counterOfferAmount').value)
            }
          ]
        }
      };
      const data = {
        payload,
        analyticsPayload
      };
      this.action.emit(data);
    } else {
      const errors = [];
      if (this.incorrectAmount) {
        errors.push(this.counterOfferAmountError);
      }
      if (this.isAmountEmpty) {
        errors.push(this.emptyOfferAmountError);
      }
      const analyticsPayload = {
        formValues: {
          name: 'Counter Offer',
          status: 'error',
          errors: errors,
          type: '',
          step: '1',
          stepname: '',
          items: [
            {
              allow: 'yes',
              error: null,
              name: 'Vehicle UUID',
              track: 'yes',
              value: this.inventoryItem ? this.inventoryItem.inventoryItemUuid : null
            },
            {
              allow: 'yes',
              error: this.counterForm.get('counterOfferAmount').invalid
                ? 'Counter amount is required.'
                : this.incorrectAmount
                ? this.counterOfferAmountError
                : null,
              name: 'Buyer Offer',
              track: 'yes',
              value: parseInt(this.counterForm.get('counterOfferAmount').value)
                ? parseInt(this.counterForm.get('counterOfferAmount').value)
                : null
            }
          ]
        }
      };
      this.analyticsService.trackEndpoint(analyticsPayload);
    }
  }

  validateAmount() {
    this.localErrorMessage = null;
    this.inputConfig.localErrorMessage = this.localErrorMessage;
    this.isAmountEmpty = false;
    this.incorrectAmount = false;
    const counterValue = this.counterForm.get('counterOfferAmount').value;

    if (!counterValue) {
      this.isAmountEmpty = true;
    } else {
      const counterAmount = parseInt(counterValue);
      this.isAmountEmpty = false;
      counterAmount <= this.offer.amount ? (this.incorrectAmount = true) : (this.incorrectAmount = false);
    }
    this.inputConfig.inputInvalidClass = this.invalidClass;
  }

  formatCurrency(currency) {
    return this.currencyFormatService.formatCurrency(currency, false).replaceAll('-', '');
  }

  ngOnDestroy(): void {
    this.localErrorMessage = null;
    this.inputConfig.localErrorMessage = this.localErrorMessage;
    this.inventoryItemDataService._localErrorState.next({ message: null, offerUuid: null });

    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
